import React from 'react';
import PropTypes from 'prop-types';
import LoadingSpinner from './LoadingSpinner';
import styles from './LoadingState.module.css';

/**
 * Standardized LoadingState component for showing loading states
 *
 * Displays a spinner with optional message
 *
 * Examples:
 *
 * Basic loading state:
 * <LoadingState />
 *
 * With custom message:
 * <LoadingState message="Loading users..." />
 *
 * Full page variant:
 * <LoadingState variant="fullPage" message="Please wait..." />
 *
 * Inline variant:
 * <LoadingState variant="inline" size="sm" message="Loading..." />
 */
const LoadingState = ({
  message = 'Loading...',
  size = 'medium', // 'small', 'medium', 'large'
  variant = 'inline', // 'inline', 'fullPage'
  className = '',
  spinnerColor = 'primary',
  ...rest
}) => {
  const containerClasses = [
    styles.loadingState,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} {...rest}>
      <LoadingSpinner size={size} color={spinnerColor} />
      {message && <p className={styles.message}>{message}</p>}
    </div>
  );
};

LoadingState.propTypes = {
  /** Message to display during loading */
  message: PropTypes.string,

  /** Size of spinner */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Display variant */
  variant: PropTypes.oneOf(['inline', 'fullPage']),

  /** Additional CSS class */
  className: PropTypes.string,

  /** Spinner color */
  spinnerColor: PropTypes.oneOf(['primary', 'secondary', 'white']),
};

export default LoadingState;
